(function () {
  'use strict';

  /*
   * UltimaPeak -> CheckoutChamp attribution + quiz metadata bridge
   *
   * Client requirements covered here:
   * - Attribution must reach every direct get.ultimapeak.com/shopify/checkout* URL.
   * - Source is the funnel/page the visitor ENTERED on, not the page they buy from.
   * - Source is persisted once per browser tab/session and never overwritten by internal navigation.
   * - Funnel codes are preferred. Unknown paths use F-UNCODED-<path> so gaps stay visible.
   * - Paid-ad parameters are kept in their normal names AND mirrored to cc_custom__* so
   *   CheckoutChamp can retain them on the order where configured.
   * - Quiz answers are converted to frozen random codes before they leave the browser.
   *   Readable answer slugs never reach CheckoutChamp or GA4.
   */

  var STORAGE_KEY = 'ultimapeak_champ_attribution_v2';
  var LEGACY_STORAGE_KEY = 'ultimapeak_champ_attribution_v1';
  var QUIZ_STORAGE_KEY = 'ultimapeak_quiz_answers_v1';
  var QUIZ_CONTEXT_PARAM = 'quiz_source';
  var CHAMP_HOST = 'get.ultimapeak.com';
  var CHAMP_PATH_PREFIX = '/shopify/checkout';
  var T0004_VIEW = 't0004-dpxzb';
  var T0004_HERO_PATH = '/products/ultimapeak-daily-gummies';
  var T0004_ALIAS_PATH = '/products/ultimapeak-daily-gummies-view-t0004-dpxzb';
  var T0006_VIEW = 't0006-bujdb';
  var T0006_HERO_PATH = '/products/ultimapeak-daily-gummies';

  /*
   * Approved first-touch path -> funnel code map.
   * Locale prefixes (for example /en-nl/) and ?view= test variants are normalized
   * to the main page before lookup. Unknown pages keep the F-UNCODED-<path> fallback.
   */
  var FUNNEL_CODE_BY_PATH = {
    '/pages/shilajit-gummies-2': 'F-LASTLONGERNL',
    '/pages/3-reasons-why-blood-flow-performance-v1': 'F-3REASONSBF',
    '/pages/3-reasons-why-blood-flow-performance-v3': 'F-3REASONSBF3',
    '/pages/5-reasons-why-blue-pill': 'F-5REASONS',
    '/pages/6-in-1-gummies-listcle': 'F-6IN1LISTICLE',
    '/pages/adv-1': 'F-BLUEPILLADV',
    '/pages/increase-d-size-naturally': 'F-DSIZEADV',
    '/pages/lis-1': 'F-BLOODFLOW',
    '/pages/listicle-5-ways': 'F-5WAYS',
    '/pages/lp': 'F-DSIZELP',
    '/pages/natural-performance-support': 'F-LASTLONGER',
    '/pages/performance-gummies-satisfy-her': 'F-SATISFYHER',
    '/pages/questionaire-funnel': 'F-QUESTQUIZ',
    '/pages/quiz-funnel-v2': 'F-SPARTANQUIZ',
    '/pages/quiz-funnel-v2-view-t0003-wtj4b': 'F-SPARTANQUIZ',
    '/pages/shipping-free-low-price-offer-pages-1': 'F-LOWPRICE1',
    '/products/gummies-coc': 'F-COCPDP',
    '/products/performance-gummies-6-in-1-support-upgraded-formula': 'F-SIXINONE',
    '/products/performance-gummies-remake': 'F-REMAKEPDP',
    '/products/ultimapeak-daily-gummies': 'F-HEROPDP',
    '/products/ultimapeak-daily-gummies-view-t0004-dpxzb': 'F-HEROPDP',
    '/products/t0005-3af2a': 'F-HEROPDP',
    '/products/ultimapeak-daily-gummies-view-t0005-3af2b-hero': 'F-HEROPDP',
    '/products/performance-gummies-6-in-1-support-upgraded-formula-t0006': 'F-HEROPDP',
    '/products/t0005-3af2a1': 'F-REMAKEPDP',
    '/products/performance-gummies-remake-view-t0005-3af2b-remake': 'F-REMAKEPDP',
    '/products/t0005-3af2a2': 'F-SPARTANQUIZ',
    '/products/ultimapeak™-performance-gummies-upgrade-quiz-1-view-t0005-3af2b-quiz': 'F-SPARTANQUIZ',
    '/products/ultimapeak-daily-gummies-new-offer': 'F-LOWPRICE2',
    '/': 'F-HOMEPAGE'
  };

  var SUPPORTED_QUIZ_SOURCES = {
    quiz_funnel_v2: true,
    questionaire_funnel: true
  };

  var exactAttributionKeys = {
    fbclid: true,
    gclid: true,
    dclid: true,
    gbraid: true,
    wbraid: true,
    msclkid: true,
    ttclid: true,
    twclid: true,
    tw_adid: true,
    tw_source: true,
    li_fat_id: true,
    sccid: true,
    tblci: true,
    trafficsource: true,
    traffic_source: true,
    clickid: true,
    click_id: true,
    applovin_click_id: true,
    axon_click_id: true
  };

  function isAttributionParameter(name) {
    if (!name) return false;

    var key = String(name).toLowerCase();
    if (key.indexOf('cc_custom__') === 0) {
      key = key.slice('cc_custom__'.length);
    }

    return Boolean(
      exactAttributionKeys[key] ||
      key.indexOf('utm_') === 0 ||
      key.indexOf('gad_') === 0 ||
      key.indexOf('taboola_') === 0 ||
      key.indexOf('applovin_') === 0 ||
      key.indexOf('axon_') === 0 ||
      key.indexOf('click_id') !== -1 ||
      key.indexOf('clickid') !== -1 ||
      /clid$/.test(key)
    );
  }

  function cleanPath(pathname) {
    var path = String(pathname || '/');
    if (path.charAt(0) !== '/') path = '/' + path;
    return path || '/';
  }

  function normalizeFunnelPath(pathname) {
    var path = cleanPath(pathname).replace(/\/{2,}/g, '/');

    // Shopify locale prefixes such as /en-nl/, /en-gb/ or /nl/ use the main
    // page's funnel code. Query-string test variants (?view=...) are already
    // excluded because this function only receives pathname.
    path = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
    if (!path) path = '/';

    if (path.length > 1) path = path.replace(/\/+$/, '');
    return path || '/';
  }

  function normalizeSourceCode(value) {
    var source = String(value || '').trim();
    if (!source) return '';
    return source.slice(0, 180);
  }

  function normalizeSlug(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 100);
  }

  function normalizeFieldName(value) {
    return normalizeSlug(value)
      .replace(/-/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .slice(0, 70);
  }

  function readJsonStorage(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function writeJsonStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Attribution must never block the shopper if storage is unavailable.
    }
  }

  function getConfiguredFunnelMap() {
    var external = window.UltimaPeakFunnelCodes;
    if (external && typeof external === 'object') return external;
    return FUNNEL_CODE_BY_PATH;
  }

  function getExplicitFunnelCode() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      var explicit = params.get('funnel_code') || params.get('funnel_source') || params.get('cc_custom__source') || params.get('source') || '';
      explicit = normalizeSourceCode(explicit);
      return /^F-/i.test(explicit) ? explicit : '';
    } catch (error) {
      return '';
    }
  }

  function resolveCurrentEntrySource() {
    var path = normalizeFunnelPath(window.location.pathname);
    var explicit = getExplicitFunnelCode();
    if (explicit) return explicit;

    var map = getConfiguredFunnelMap();
    var mapped = map && normalizeSourceCode(map[path]);
    if (mapped) return mapped;

    return 'F-UNCODED-' + path;
  }

  /*
   * T0004 is the Hero PDP story test. Shopify ?view=t0004-dpxzb is a query
   * parameter, not a pathname, so adding a "-view-" URL to the path map alone
   * cannot identify the real variation. The prior first-touch session can also
   * contain F-HOMEPAGE from a preview/navigation before the visitor enters T0004.
   *
   * Only when this exact test starts, pin its funnel code to F-HEROPDP and
   * remember the *actual* test page URL for CheckoutChamp. Preserve collected
   * ad-click attribution and keep all other funnels' first-touch rules intact.
   * The section check also covers the standalone T0004 story page included in
   * earlier theme exports, which currently links on to the Hero PDP.
   */
  function isT0004Landing() {
    var path = normalizeFunnelPath(window.location.pathname);
    if (path === T0004_ALIAS_PATH) return true;

    if (path === T0004_HERO_PATH) {
      try {
        if (new URLSearchParams(window.location.search || '').get('view') === T0004_VIEW) {
          return true;
        }
      } catch (error) {
        // A malformed query must not interfere with normal attribution.
      }
    }

    return Boolean(document.querySelector &&
      document.querySelector('.tap-through-story-section .t4-stage[data-story-name]'));
  }

  /*
   * T0006 is a ?view= version of the Hero PDP, NOT a different pathname.
   * A prior F-HOMEPAGE session must not mask the T0006 funnel source.
   * Return the actual variation entry URL for the checkout order.
   */
  function getT0006LandingContext() {
    if (normalizeFunnelPath(window.location.pathname) !== T0006_HERO_PATH) return null;
    try {
      var params = new URLSearchParams(window.location.search || '');
      if (params.get('view') !== T0006_VIEW) return null;
      return { view: T0006_VIEW, pageAddress: window.location.pathname + window.location.search };
    } catch (error) {
      return null;
    }
  }

  function isAnotherTestView() {
    try {
      var view = new URLSearchParams(window.location.search || '').get('view');
      return Boolean(view && view !== T0004_VIEW);
    } catch (error) {
      return false;
    }
  }

  function migrateLegacyAttribution() {
    var legacy = readJsonStorage(LEGACY_STORAGE_KEY);
    if (!legacy) return {};

    var migrated = {};
    Object.keys(legacy).forEach(function (key) {
      if (isAttributionParameter(key) && legacy[key] !== '') {
        var plainKey = String(key).replace(/^cc_custom__/, '');
        if (!(plainKey in migrated)) migrated[plainKey] = String(legacy[key]);
      }
    });
    return migrated;
  }

  function readSessionState() {
    var state = readJsonStorage(STORAGE_KEY);
    if (state) {
      state.attribution = state.attribution && typeof state.attribution === 'object'
        ? state.attribution
        : {};
      return state;
    }

    return {
      version: 2,
      entrySource: '',
      entryPath: '',
      capturedAt: 0,
      attribution: migrateLegacyAttribution()
    };
  }

  function captureEntryContext() {
    var state = readSessionState();
    var changed = false;

    if (!state.entrySource) {
      state.entrySource = resolveCurrentEntrySource();
      state.entryPath = cleanPath(window.location.pathname);
      state.capturedAt = Date.now();
      changed = true;
    }

    // T0004-only: start/recover the story funnel, even if a visit to the
    // homepage was previously stored in this browser tab. Do not reset UTMs.
    if (isT0004Landing()) {
      if (state.entrySource !== 'F-HEROPDP') {
        state.entrySource = 'F-HEROPDP';
        state.entryPath = cleanPath(window.location.pathname);
        state.capturedAt = Date.now();
        changed = true;
      }
      if (!state.t0004 || state.t0004.view !== T0004_VIEW) {
        state.t0004 = {
          view: T0004_VIEW,
          variant: 'story',
          pageAddress: window.location.pathname + window.location.search
        };
        changed = true;
      }
    }

    // T0006 only: restore the correct Hero PDP source even if the visitor
    // opened the homepage earlier in the same tab. Keep all paid-ad tags.
    var t0006Landing = getT0006LandingContext();
    if (t0006Landing) {
      if (state.entrySource !== 'F-HEROPDP') {
        state.entrySource = 'F-HEROPDP';
        state.entryPath = cleanPath(window.location.pathname);
        state.capturedAt = Date.now();
        changed = true;
      }
      if (!state.t0006 || state.t0006.view !== T0006_VIEW) {
        state.t0006 = {
          view: T0006_VIEW,
          variant: 'free_gift_tiers',
          pageAddress: t0006Landing.pageAddress
        };
        changed = true;
      }
    }

    var current;
    try {
      current = new URLSearchParams(window.location.search || '');
    } catch (error) {
      current = new URLSearchParams();
    }

    current.forEach(function (value, key) {
      if (!isAttributionParameter(key) || value === '') return;

      var plainKey = String(key).replace(/^cc_custom__/, '');

      // First-touch attribution: once captured in this tab/session, internal
      // navigation and later page parameters do not overwrite the entry values.
      if (!state.attribution[plainKey]) {
        state.attribution[plainKey] = String(value);
        changed = true;
      }
    });

    if (changed) writeJsonStorage(STORAGE_KEY, state);
    return state;
  }

  function getStoredSource() {
    return captureEntryContext().entrySource || resolveCurrentEntrySource();
  }

  function getStoredAttribution() {
    return captureEntryContext().attribution || {};
  }

  function buildStoredQuizState(parsed, quizName) {
    if (!parsed || !parsed.quizzes || !quizName) return null;

    var quizState = parsed.quizzes[quizName];
    if (!quizState || !quizState.answers) return null;

    return {
      version: parsed.version || 1,
      quizName: quizName,
      answers: quizState.answers,
      updatedAt: Number(quizState.updatedAt || 0),
      completedAt: Number(quizState.completedAt || 0)
    };
  }

  function readStoredQuizState() {
    var parsed = readJsonStorage(QUIZ_STORAGE_KEY);
    if (!parsed || !parsed.quizzes) return null;
    return buildStoredQuizState(parsed, parsed.lastQuiz);
  }

  function markQuizCompleted(quizName) {
    if (!quizName || !SUPPORTED_QUIZ_SOURCES[quizName]) return null;

    var parsed = readJsonStorage(QUIZ_STORAGE_KEY);
    if (!parsed || !parsed.quizzes) return null;

    var quizState = parsed.quizzes[quizName];
    if (!quizState || !quizState.answers || !Object.keys(quizState.answers).length) return null;

    if (!quizState.completedAt) {
      quizState.completedAt = Date.now();
      parsed.quizzes[quizName] = quizState;
      parsed.lastQuiz = quizName;
      writeJsonStorage(QUIZ_STORAGE_KEY, parsed);
    }

    return buildStoredQuizState(parsed, quizName);
  }

  function readLatestCompletedQuizState() {
    var parsed = readJsonStorage(QUIZ_STORAGE_KEY);
    if (!parsed || !parsed.quizzes) return null;

    var latest = null;

    Object.keys(parsed.quizzes).forEach(function (quizName) {
      if (!SUPPORTED_QUIZ_SOURCES[quizName]) return;

      var state = buildStoredQuizState(parsed, quizName);
      if (!state || !state.completedAt) return;

      if (!latest || state.completedAt > latest.completedAt) {
        latest = state;
      }
    });

    return latest;
  }

  function getQuizCheckoutContext() {
    var params;
    try {
      params = new URLSearchParams(window.location.search || '');
    } catch (error) {
      params = new URLSearchParams();
    }

    var quizSource = String(params.get(QUIZ_CONTEXT_PARAM) || '').trim();

    /*
     * Reaching the PDP with a valid ?quiz_source= is the existing quiz-complete
     * handoff. Persist that completion once in sessionStorage. This lets later
     * PDP visits in the same tab keep the finished quiz answers even after the
     * quiz_source query parameter is gone. Partial/abandoned quizzes are never
     * attached because they do not have completedAt.
     */
    if (quizSource && SUPPORTED_QUIZ_SOURCES[quizSource]) {
      var completedState = markQuizCompleted(quizSource);
      if (completedState) {
        return {
          source: quizSource,
          quizState: completedState
        };
      }
    }

    var storedCompletedState = readLatestCompletedQuizState();
    if (!storedCompletedState) return null;

    return {
      source: storedCompletedState.quizName,
      quizState: storedCompletedState
    };
  }

  function getQuizCodeQuestion(quizName, questionId) {
    var root = window.UltimaPeakQuizCodes;
    var quizzes = root && root.quizzes;
    var quiz = quizzes && quizzes[quizName];
    var questions = quiz && quiz.questions;
    return questions && questions[questionId] ? questions[questionId] : null;
  }

  function getQuestionCode(quizName, questionId) {
    var question = getQuizCodeQuestion(quizName, questionId);
    return question && question.code ? String(question.code) : '';
  }

  function getAnswerCode(quizName, questionId, answerId) {
    var question = getQuizCodeQuestion(quizName, questionId);
    var answer = question && question.answers && question.answers[answerId];
    return answer && answer.code ? String(answer.code) : '';
  }

  function warnMissingQuizCode(quizName, questionId, answerId) {
    if (!window.console || typeof window.console.warn !== 'function') return;
    window.console.warn(
      'UltimaPeak tracking: missing frozen quiz code; readable value was not sent.',
      { quiz: quizName, question: questionId, answer: answerId || '' }
    );
  }

  function serializeQuizAnswers(answers, quizName) {
    if (!answers || typeof answers !== 'object') return '';

    return Object.keys(answers)
      .sort()
      .map(function (questionId) {
        var questionCode = getQuestionCode(quizName, questionId);
        if (!questionCode) {
          warnMissingQuizCode(quizName, questionId, '');
          return '';
        }

        var rawValues = Array.isArray(answers[questionId]) ? answers[questionId] : [answers[questionId]];
        var values = rawValues
          .map(function (answerId) {
            var answerCode = getAnswerCode(quizName, questionId, String(answerId));
            if (!answerCode) warnMissingQuizCode(quizName, questionId, String(answerId));
            return answerCode;
          })
          .filter(Boolean);

        if (!values.length) return '';
        return questionCode + '=' + values.join(',');
      })
      .filter(Boolean)
      .join(';');
  }

  function addQuizParams(destination, quizState) {
    if (!quizState || !quizState.answers) return;

    var quizName = String(quizState.quizName || '');
    var serializedAnswers = serializeQuizAnswers(quizState.answers, quizName);
    if (!serializedAnswers) return;

    destination.searchParams.set('cc_custom__quiz_name', normalizeSlug(quizName));
    destination.searchParams.set('cc_custom__quiz_answers', serializedAnswers);
    destination.searchParams.set('cc_custom__quiz_version', String(quizState.version || 1));

    Object.keys(quizState.answers).forEach(function (questionId) {
      var questionCode = getQuestionCode(quizName, questionId);
      if (!questionCode) {
        warnMissingQuizCode(quizName, questionId, '');
        return;
      }

      var rawValues = Array.isArray(quizState.answers[questionId])
        ? quizState.answers[questionId]
        : [quizState.answers[questionId]];
      var values = rawValues
        .map(function (answerId) {
          var answerCode = getAnswerCode(quizName, questionId, String(answerId));
          if (!answerCode) warnMissingQuizCode(quizName, questionId, String(answerId));
          return answerCode;
        })
        .filter(Boolean);
      if (!values.length) return;

      destination.searchParams.set('cc_custom__q_' + questionCode, values.join(','));
    });
  }


  /*
   * T0005 uses six dedicated Shopify product URLs rather than ?view= query
   * variants. Keep the A/B metadata isolated to those six URLs only.
   *
   * Control:
   *   /products/t0005-3af2a   -> hero page clone
   *   /products/t0005-3af2a1  -> remake clone
   *   /products/t0005-3af2a2  -> quiz product page clone
   *
   * Countdown variation:
   *   /products/t0005-3af2b
   *   /products/t0005-3af2b1
   *   /products/t0005-3af2b2
   */
  var T0005_PAGE_BY_PATH = {
    '/products/t0005-3af2a': {
      view: 't0005-3af2a',
      variant: 'control',
      template: 'ultimapeak-daily-gummies-t0005-3af2a'
    },
    '/products/t0005-3af2b': {
      view: 't0005-3af2b',
      variant: 'countdown',
      template: 'ultimapeak-daily-gummies-t0005-3af2b'
    },
    '/products/t0005-3af2a1': {
      view: 't0005-3af2a1',
      variant: 'control',
      template: 'ultimapeak-daily-gummies-remake-t0005-3af2a'
    },
    '/products/t0005-3af2b1': {
      view: 't0005-3af2b1',
      variant: 'countdown',
      template: 'ultimapeak-daily-gummies-remake-t0005-3af2b'
    },
    '/products/t0005-3af2a2': {
      view: 't0005-3af2a2',
      variant: 'control',
      template: 'below-the-fold-sex-profil-t0005-3af2a'
    },
    '/products/t0005-3af2b2': {
      view: 't0005-3af2b2',
      variant: 'countdown',
      template: 'below-the-fold-sex-profil-t0005-3af2b'
    }
  };

  function getT0005PageContext() {
    var path = normalizeFunnelPath(window.location.pathname);
    var config = T0005_PAGE_BY_PATH[path];
    if (!config) return null;

    return {
      view: config.view,
      variant: config.variant,
      template: config.template,
      pageAddress: window.location.pathname + window.location.search
    };
  }

  function addT0005PageParams(destination) {
    var context = getT0005PageContext();
    if (!context) return;

    destination.searchParams.set('cc_custom__test', 'T0005');
    destination.searchParams.set('cc_custom__test_variant', context.variant);
    destination.searchParams.set('cc_custom__test_view', context.view);
    destination.searchParams.set('cc_custom__test_template', context.template);
    destination.searchParams.set('cc_custom__page_address', context.pageAddress);
  }

  function getT0004PageContext() {
    var state = captureEntryContext();
    if (!state.t0004 || state.t0004.view !== T0004_VIEW) return null;

    // Never mislabel a separate active test (e.g. T0005 or T0006) as T0004.
    if (getT0005PageContext() || isAnotherTestView()) return null;

    return {
      view: T0004_VIEW,
      variant: 'story',
      pageAddress: String(state.t0004.pageAddress || '')
    };
  }

  function addT0004PageParams(destination) {
    var context = getT0004PageContext();
    if (!context) return;

    destination.searchParams.set('cc_custom__test', 'T0004');
    destination.searchParams.set('cc_custom__test_variant', context.variant);
    destination.searchParams.set('cc_custom__test_view', context.view);
    if (context.pageAddress) {
      destination.searchParams.set('cc_custom__page_address', context.pageAddress);
    }
  }

  /*
   * Keep T0006 order metadata separate from T0004/T0005. The original
   * page address is retained even when the checkout URL is built later.
   */
  function getT0006PageContext() {
    var state = captureEntryContext();
    if (!state.t0006 || state.t0006.view !== T0006_VIEW) return null;
    if (normalizeFunnelPath(window.location.pathname) !== T0006_HERO_PATH) return null;
    if (getT0005PageContext() || isT0004Landing()) return null;
    try {
      var currentView = new URLSearchParams(window.location.search || '').get('view');
      if (currentView && currentView !== T0006_VIEW) return null;
    } catch (error) {
      return null;
    }
    return state.t0006;
  }

  function addT0006PageParams(destination) {
    var context = getT0006PageContext();
    if (!context) return;
    destination.searchParams.set('cc_custom__test', 'T0006');
    destination.searchParams.set('cc_custom__test_variant', context.variant);
    destination.searchParams.set('cc_custom__test_view', context.view);
    if (context.pageAddress) {
      destination.searchParams.set('cc_custom__page_address', context.pageAddress);
    }
  }

  function isChampCheckoutUrl(urlObject) {
    return Boolean(
      urlObject &&
      urlObject.hostname === CHAMP_HOST &&
      urlObject.pathname.indexOf(CHAMP_PATH_PREFIX) === 0
    );
  }

  /*
   * Some existing buy links contain a second '?' inside the products value,
   * for example: products=246:1;?utm_source=shopify_coc.
   * Once URLSearchParams touches that URL it becomes
   * products=246%3A1%3B%3Futm_source%3Dshopify_coc, which hides the UTM inside
   * products. Repair it before applying attribution.
   */
  function repairEmbeddedProductsQuery(destination) {
    var products = destination.searchParams.get('products');
    if (!products) return;

    var markerIndex = products.indexOf('?');
    if (markerIndex === -1) return;

    var productValue = products.slice(0, markerIndex);
    var embeddedQuery = products.slice(markerIndex + 1).replace(/^\?+/, '');
    destination.searchParams.set('products', productValue);

    if (!embeddedQuery) return;

    var embeddedParams = new URLSearchParams(embeddedQuery);
    embeddedParams.forEach(function (value, key) {
      var cleanKey = String(key || '').replace(/^\?+/, '');
      if (!cleanKey) return;
      if (!destination.searchParams.has(cleanKey)) {
        destination.searchParams.set(cleanKey, value);
      }
    });
  }

  function ccCustomName(key) {
    var clean = String(key || '').replace(/^cc_custom__/, '');
    return 'cc_custom__' + clean.replace(/[^a-zA-Z0-9_]/g, '_');
  }

  function applyAttributionParams(destination) {
    var attribution = getStoredAttribution();

    // Stored entry attribution wins over hard-coded/internal UTMs on checkout links.
    Object.keys(attribution).forEach(function (key) {
      var value = attribution[key];
      if (value === '' || value == null) return;
      destination.searchParams.set(key, value);
      destination.searchParams.set(ccCustomName(key), value);
    });

    // If a checkout URL itself has an attribution parameter that was not present
    // at entry, mirror it to cc_custom__* as an order-retention fallback.
    var paramsToMirror = [];
    destination.searchParams.forEach(function (value, key) {
      if (key.indexOf('cc_custom__') === 0) return;
      if (!isAttributionParameter(key) || value === '') return;
      paramsToMirror.push([key, value]);
    });
    paramsToMirror.forEach(function (pair) {
      destination.searchParams.set(ccCustomName(pair[0]), pair[1]);
    });
  }

  function decorateChampUrl(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;

    var destination;
    try {
      destination = new URL(rawUrl, window.location.href);
    } catch (error) {
      return rawUrl;
    }

    if (!isChampCheckoutUrl(destination)) return rawUrl;

    repairEmbeddedProductsQuery(destination);
    captureEntryContext();

    var source = getStoredSource();
    destination.searchParams.set('source', source);
    destination.searchParams.set('cc_custom__source', source);

    addT0005PageParams(destination);
    addT0004PageParams(destination);
    addT0006PageParams(destination);
    applyAttributionParams(destination);

    var quizContext = getQuizCheckoutContext();
    if (quizContext) addQuizParams(destination, quizContext.quizState);

    return destination.toString();
  }

  function decorateElement(element) {
    if (!element || element.nodeType !== 1) return;

    if (element.matches && element.matches('a[href]')) {
      var href = element.getAttribute('href');
      var decoratedHref = decorateChampUrl(href);
      if (decoratedHref !== href) element.setAttribute('href', decoratedHref);
    }

    if (element.matches && element.matches('form[action]')) {
      var action = element.getAttribute('action');
      var decoratedAction = decorateChampUrl(action);
      if (decoratedAction !== action) element.setAttribute('action', decoratedAction);
    }
  }

  function decorateTree(root) {
    if (!root || root.nodeType !== 1) return;

    decorateElement(root);
    if (!root.querySelectorAll) return;

    root.querySelectorAll('a[href], form[action]').forEach(function (element) {
      decorateElement(element);
    });
  }

  function decorateInteractionTarget(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var link = target.closest('a[href]');
    if (link) decorateElement(link);
  }

  captureEntryContext();

  window.UltimaPeakTracking = window.UltimaPeakTracking || {};
  window.UltimaPeakTracking.decorateChampUrl = decorateChampUrl;
  window.UltimaPeakTracking.captureAttribution = captureEntryContext;
  window.UltimaPeakTracking.getChampSource = getStoredSource;
  window.UltimaPeakTracking.getAttribution = getStoredAttribution;
  window.UltimaPeakTracking.getSessionState = readSessionState;
  window.UltimaPeakTracking.getT0004PageContext = getT0004PageContext;
  window.UltimaPeakTracking.getQuizCheckoutState = readStoredQuizState;
  window.UltimaPeakTracking.getQuizCheckoutContext = getQuizCheckoutContext;
  window.UltimaPeakTracking.funnelCodeMap = FUNNEL_CODE_BY_PATH;
  window.UltimaPeakTracking.normalizeFunnelPath = normalizeFunnelPath;
  window.UltimaPeakTracking.getQuizQuestionCode = getQuestionCode;
  window.UltimaPeakTracking.getQuizAnswerCode = getAnswerCode;

  if (document.documentElement) decorateTree(document.documentElement);

  document.addEventListener('DOMContentLoaded', function () {
    decorateTree(document.documentElement);
  });

  ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(function (eventName) {
    document.addEventListener(eventName, decorateInteractionTarget, true);
  });

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      decorateInteractionTarget(event);
    },
    true
  );

  if (document.documentElement && window.MutationObserver) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          decorateElement(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(function (node) {
          decorateTree(node);
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'action']
    });
  }
})();
